import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Youtube, Twitter, MessageCircle, Gamepad2, Globe, Volume2, VolumeX } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { trackPage } from "@/lib/api";
import { soundOn, setSoundOn, playClick } from "@/lib/sounds";
import HomeMusic from "@/components/HomeMusic";

export function Logo({ size = "md" }) {
  const { config } = useApp();
  const url = config?.branding?.logo_url;
  const cls = size === "lg" ? "w-12 h-12 text-xl" : "w-9 h-9 text-base";
  return (
    <div className={`${cls} rounded-lg bg-[#0b1f3a] flex items-center justify-center overflow-hidden shrink-0`}>
      {url ? <img src={url} alt="LosTDRKC" className="w-full h-full object-contain p-1" /> : <span className="text-white font-black font-heading">L</span>}
    </div>
  );
}

export default function PublicLayout() {
  const { config, user, logout } = useApp();
  const [open, setOpen] = useState(false);
  const [ann, setAnn] = useState([]);
  const [snd, setSnd] = useState(soundOn());
  const loc = useLocation();

  useEffect(() => {
    trackPage(loc.pathname);
    setOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    api.get("/public/announcements").then(({ data }) => setAnn(data.announcements || [])).catch(() => {});
  }, [loc.pathname]);

  const nav = [
    { to: "/", label: "Home" },
    { to: "/leaderboard", label: "Leaderboard" },
    { to: "/rules", label: "Rules" },
    { to: "/results", label: "Results" },
    { to: "/contact", label: "Contact" },
  ];
  const socials = config?.socials || {};

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="tricolor-bar h-1 w-full" />
      {ann.filter((a) => a.active).slice(0, 1).map((a) => (
        <div key={a.id} data-testid="announcement-banner" className={`text-center text-sm font-medium py-2 px-4 ${a.type === "warning" || a.type === "emergency" ? "bg-red-50 text-red-700" : "bg-[#003366]/5 text-[#003366]"}`}>
          <span className="font-bold mr-2">{a.title}</span>{a.message}
        </div>
      ))}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <Logo />
            <div className="leading-tight">
              <div className="font-heading font-black text-slate-900 tracking-tight">LosTDRKC <span className="text-[#FF9933]">Quiz</span></div>
              <div className="text-[10px] font-bold tracking-[0.25em] text-slate-400 uppercase">Independence Day Special</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-7">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `text-sm font-semibold transition-colors ${isActive ? "text-[#FF9933]" : "text-slate-600 hover:text-slate-900"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => { const next = !snd; setSoundOn(next); setSnd(next); if (next) playClick(); }} data-testid="sound-toggle" aria-label="Toggle sound effects"
              className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:text-[#FF9933] hover:border-[#FF9933] transition-colors">
              {snd ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            {user ? (
              <>
                <Link to="/dashboard" data-testid="nav-dashboard-btn" className="px-4 py-2 text-sm font-bold rounded-lg bg-[#003366] text-white hover:bg-[#00284f] transition-colors">Dashboard</Link>
                <button onClick={logout} data-testid="nav-logout-btn" className="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-red-600 transition-colors">Logout</button>
              </>
            ) : (
              <Link to="/auth" data-testid="nav-register-btn" className="px-5 py-2.5 text-sm font-bold rounded-lg bg-[#FF9933] text-white hover:bg-[#E6892E] transition-colors shadow-sm">
                Register Now
              </Link>
            )}
          </div>
          <button className="md:hidden p-2" data-testid="nav-mobile-menu-btn" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-slate-200 bg-white px-5 py-4 flex flex-col gap-3">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-mobile-${n.label.toLowerCase()}`} className="text-sm font-semibold text-slate-700 py-1">{n.label}</NavLink>
            ))}
            {user ? (
              <Link to="/dashboard" data-testid="nav-mobile-dashboard" className="text-sm font-bold text-[#003366] py-1">Dashboard</Link>
            ) : (
              <Link to="/auth" data-testid="nav-mobile-register" className="text-sm font-bold text-[#FF9933] py-1">Register Now</Link>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>
      {!loc.pathname.startsWith("/quiz") && <HomeMusic />}

      <footer className="border-t border-slate-200 bg-slate-50 mt-20">
        <div className="tricolor-bar h-1 w-full" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4"><Logo size="lg" />
              <div>
                <div className="font-heading font-black text-lg text-slate-900">LosTDRKC Quiz</div>
                <div className="text-xs text-slate-500">{config?.competition?.title}</div>
              </div>
            </div>
            <p className="text-sm text-slate-500 max-w-sm">India's premium community quiz competition. Test your knowledge, beat the clock, climb the leaderboard.</p>
            <div className="flex gap-3 mt-5">
              {socials.instagram && <a data-testid="footer-instagram" href={socials.instagram} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:text-[#FF9933] hover:border-[#FF9933] transition-colors"><Instagram className="w-4 h-4" /></a>}
              {socials.discord && <a data-testid="footer-discord" href={socials.discord} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:text-[#003366] hover:border-[#003366] transition-colors"><MessageCircle className="w-4 h-4" /></a>}
              {socials.youtube && <a data-testid="footer-youtube" href={socials.youtube} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:text-red-600 hover:border-red-600 transition-colors"><Youtube className="w-4 h-4" /></a>}
              {socials.twitter && <a data-testid="footer-twitter" href={socials.twitter} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-900 hover:border-slate-900 transition-colors"><Twitter className="w-4 h-4" /></a>}
              {socials.website && <a data-testid="footer-website" href={socials.website} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:text-[#138808] hover:border-[#138808] transition-colors"><Globe className="w-4 h-4" /></a>}
            </div>
            {socials.minecraft && (
              <div className="mt-4 inline-flex items-center gap-2 text-xs font-mono bg-slate-900 text-green-400 px-3 py-1.5 rounded-md">
                <Gamepad2 className="w-3.5 h-3.5" /> {socials.minecraft}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase mb-4">Competition</div>
            <div className="flex flex-col gap-2.5 text-sm">
              <Link to="/leaderboard" className="text-slate-600 hover:text-[#FF9933] transition-colors">Leaderboard</Link>
              <Link to="/rules" className="text-slate-600 hover:text-[#FF9933] transition-colors">Competition Rules</Link>
              <Link to="/results" className="text-slate-600 hover:text-[#FF9933] transition-colors">Results</Link>
              <Link to="/auth" className="text-slate-600 hover:text-[#FF9933] transition-colors">Register</Link>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase mb-4">Support</div>
            <div className="flex flex-col gap-2.5 text-sm">
              <Link to="/contact" className="text-slate-600 hover:text-[#FF9933] transition-colors">Contact Us</Link>
              {config?.support_email && <a href={`mailto:${config.support_email}`} className="text-slate-600 hover:text-[#FF9933] transition-colors">{config.support_email}</a>}
              {socials.discord && <a href={socials.discord} target="_blank" rel="noreferrer" className="text-slate-600 hover:text-[#FF9933] transition-colors">Discord Support</a>}
            </div>
          </div>
        </div>
        <div className="border-t border-slate-200 py-5 text-center text-xs text-slate-400">
          © 2026 LosTDRKC. All rights reserved. • Jai Hind 🇮🇳
        </div>
      </footer>
    </div>
  );
}
